import { AxiosResponse } from "axios";
import apiClient from "../../../../shared/infrastructure/api/client";
import {
    AuthorizeUrl,
    AvailabilityWindow,
    AvailabilityWindowInput,
    BookingSessionType,
    IntegrationProvider,
    OAuthOptions,
    ScheduledSession,
    ScheduleSessionInput,
    SchedulerSettings,
    SchedulerSettingsUpdate,
    SlotList,
    TherapistSpace,
    TherapistSpaceInput,
} from "../../domain/types";

export const SchedulerAPI = {
    getSettings: (): Promise<AxiosResponse<SchedulerSettings>> =>
        apiClient.get("/scheduler/settings"),
    updateSettings: (
        payload: SchedulerSettingsUpdate
    ): Promise<AxiosResponse<SchedulerSettings>> =>
        apiClient.put("/scheduler/settings", payload),

    getAvailability: (): Promise<AxiosResponse<AvailabilityWindow[]>> =>
        apiClient.get("/scheduler/availability"),
    replaceAvailability: (
        windows: AvailabilityWindowInput[]
    ): Promise<AxiosResponse<AvailabilityWindow[]>> =>
        apiClient.put("/scheduler/availability", windows),

    /**
     * `date` is a plain `YYYY-MM-DD` in the therapist's timezone; the slots are
     * as long as a session of `sessionType`.
     */
    getSlots: (
        date: string,
        sessionType: BookingSessionType = "individual"
    ): Promise<AxiosResponse<SlotList>> =>
        apiClient.get("/scheduler/slots", { params: { date, session_type: sessionType } }),

    listSpaces: (): Promise<AxiosResponse<TherapistSpace[]>> =>
        apiClient.get("/scheduler/spaces"),
    createSpace: (
        payload: TherapistSpaceInput
    ): Promise<AxiosResponse<TherapistSpace>> =>
        apiClient.post("/scheduler/spaces", payload),
    updateSpace: (
        spaceId: string,
        payload: TherapistSpaceInput
    ): Promise<AxiosResponse<TherapistSpace>> =>
        apiClient.put(`/scheduler/spaces/${spaceId}`, payload),
    deleteSpace: (spaceId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/scheduler/spaces/${spaceId}`),

    /** `month` is `YYYY-MM`. */
    listSessions: (
        month: string,
        includeCancelled = false
    ): Promise<AxiosResponse<ScheduledSession[]>> =>
        apiClient.get("/scheduler/sessions", {
            params: { month, include_cancelled: includeCancelled },
        }),
    /**
     * The therapist's next sessions, earliest first.
     *
     * `patient_id` and `couple_patient_id` are mutually exclusive; the backend
     * refuses both, so only the one that is set is sent.
     */
    listUpcoming: (params: {
        patient_id?: string;
        couple_patient_id?: string;
        group_patient_id?: string;
        participant_id?: string;
        limit?: number;
    } = {}): Promise<AxiosResponse<ScheduledSession[]>> =>
        apiClient.get("/scheduler/sessions/upcoming", { params }),
    scheduleSession: (
        payload: ScheduleSessionInput
    ): Promise<AxiosResponse<ScheduledSession>> =>
        apiClient.post("/scheduler/sessions", payload),
    rescheduleSession: (
        sessionId: string,
        start: string
    ): Promise<AxiosResponse<ScheduledSession>> =>
        apiClient.put(`/scheduler/sessions/${sessionId}`, { start }),
    /**
     * `requestedByPatient`: the patient asked the therapist to cancel. It is
     * recorded on the session for the reports; the notices sent are the same.
     */
    cancelSession: (sessionId: string, requestedByPatient = false): Promise<AxiosResponse<void>> =>
        requestedByPatient
            ? apiClient.delete(`/scheduler/sessions/${sessionId}`, {
                  params: { requested_by_patient: true },
              })
            : apiClient.delete(`/scheduler/sessions/${sessionId}`),

    getAuthorizeUrl: (
        provider: IntegrationProvider,
        redirectUri: string,
        options: OAuthOptions = {}
    ): Promise<AxiosResponse<AuthorizeUrl>> =>
        apiClient.get(`/scheduler/integrations/${provider}/authorize-url`, {
            params: {
                redirect_uri: redirectUri,
                ...(options.transcripts ? { transcripts: true } : {}),
            },
        }),
    connectIntegration: (
        provider: IntegrationProvider,
        code: string,
        redirectUri: string
    ): Promise<AxiosResponse<SchedulerSettings>> =>
        apiClient.post(`/scheduler/integrations/${provider}/connect`, {
            code,
            redirect_uri: redirectUri,
        }),
    disconnectIntegration: (
        provider: IntegrationProvider
    ): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/scheduler/integrations/${provider}`),
};
